import { useBookingContext } from '../BookingContext'
import { Button } from '@w/components/ui/button'
import { Card, CardContent } from '@w/components/ui/card'
import { format } from 'date-fns'
import { isPreviewMode } from '@w/lib/config'
import { ClipboardCheck, Sparkles, Calendar, MapPin, User, Mail, Phone, MessageSquare, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react'

export default function ConfirmStep() {
  const {
    selectedService,
    selectedDateTime,
    customerInfo,
    bookingLoading,
    bookingError,
    createBooking,
    goToStep,
    configurationMode,
  } = useBookingContext()

  // Configuration mode: Show confirmation layout with placeholders
  if (configurationMode) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <ClipboardCheck className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold">Confirm Your Booking</h2>
            <p className="text-sm text-muted-foreground">Review your appointment details</p>
          </div>
        </div>

        <Card className="mb-6">
          <CardContent className="p-6 space-y-4">
            <div>
              <h3 className="text-sm text-muted-foreground mb-1">Service</h3>
              <p className="font-medium">[Service Name]</p>
            </div>
            <div>
              <h3 className="text-sm text-muted-foreground mb-1">Date & Time</h3>
              <p className="font-medium">[Date] at [Time]</p>
            </div>
            <div>
              <h3 className="text-sm text-muted-foreground mb-1">Location</h3>
              <p className="font-medium">[Service Address]</p>
            </div>
            <div>
              <h3 className="text-sm text-muted-foreground mb-1">Your Information</h3>
              <p className="text-sm">[Name]</p>
              <p className="text-sm">[Email]</p>
              <p className="text-sm">[Phone]</p>
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-4 justify-between">
          <Button variant="outline" size="lg" disabled className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Edit
          </Button>
          <Button size="lg" disabled className="gap-2">
            <CheckCircle className="w-4 h-4" />
            Confirm Booking
          </Button>
        </div>
      </div>
    )
  }

  if (!selectedService || !selectedDateTime || !customerInfo) {
    return (
      <div className="text-center">
        <p className="text-muted-foreground">Missing booking information</p>
        <Button onClick={() => goToStep('service')} className="mt-4">
          Start Over
        </Button>
      </div>
    )
  }

  const handleConfirm = async () => {
    await createBooking()
  }

  // Format date for display
  const formatDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number)
    const date = new Date(year, month - 1, day)
    return format(date, 'EEE, MMM d, yyyy')
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
          <ClipboardCheck className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h2 className="text-2xl font-semibold">Confirm Your Booking</h2>
          <p className="text-sm text-muted-foreground">Review your appointment details</p>
        </div>
      </div>

      {isPreviewMode() && (
        <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-900">Preview Mode</p>
            <p className="text-xs text-blue-700 mt-0.5">
              This booking will not create a real appointment.
            </p>
          </div>
        </div>
      )}

      {/* Details Grid */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* Service */}
        <Card className="transition-all duration-200 hover:shadow-md">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm text-muted-foreground mb-1">Service</h3>
                <p className="font-semibold text-lg">{selectedService.name}</p>
                {selectedService.description && (
                  <p className="text-sm text-muted-foreground mt-1">{selectedService.description}</p>
                )}
                <div className="flex gap-3 mt-2 text-sm text-muted-foreground">
                  <span>{Math.floor(selectedService.base_duration / 60)} hours</span>
                  {selectedService.base_price && <span>${selectedService.base_price}</span>}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Date & Time */}
        <Card className="transition-all duration-200 hover:shadow-md">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm text-muted-foreground mb-1">Date & Time</h3>
                <p className="font-medium">{formatDate(selectedDateTime.date)}</p>
                <p className="text-sm text-muted-foreground">{selectedDateTime.time}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Service Address */}
        <Card className="transition-all duration-200 hover:shadow-md">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm text-muted-foreground mb-1">Service Address</h3>
                <p className="font-medium">{selectedDateTime.address}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card className="transition-all duration-200 hover:shadow-md">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 text-orange-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm text-muted-foreground mb-1">Contact</h3>
                <p className="font-medium mb-2">{customerInfo.name}</p>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <Mail className="w-3 h-3" />
                    {customerInfo.email}
                  </p>
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <Phone className="w-3 h-3" />
                    {customerInfo.phone}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notes */}
        {customerInfo.notes && (
          <Card className="md:col-span-2 transition-all duration-200 hover:shadow-md">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-5 h-5 text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm text-muted-foreground mb-1">Additional Notes</h3>
                  <p className="text-sm">{customerInfo.notes}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Error Message */}
      {bookingError && (
        <div className="mb-6 p-4 bg-destructive/10 border-2 border-destructive/20 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-destructive">Booking Error</p>
            <p className="text-xs text-destructive/80 mt-0.5">{bookingError}</p>
          </div>
        </div>
      )}

      {/* Buttons */}
      <div className="flex gap-4 justify-between">
        <Button
          type="button"
          variant="outline"
          size="lg"
          onClick={() => goToStep('contact')}
          disabled={bookingLoading}
          className="gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Edit
        </Button>
        <Button onClick={handleConfirm} size="lg" disabled={bookingLoading} className="gap-2">
          {bookingLoading ? 'Confirming...' : 'Confirm Booking'}
          <CheckCircle className="w-4 h-4" />
        </Button>
      </div>
    </div>
  )
}
